import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import BlankPaperPreview from "./BlankPaperPreview";
import ApparatModeWarning from "../print-settings/ApparatModeWarning";
import KioskModal from "../../components/modals/KioskModal";
import { fetchBlankDocument } from "../../store/actions/documentsActions";
import {
    createPaymentAction,
    fetchPriceApparat,
} from "../../store/actions/paymentsActions";
import { isFreePrintEnabled } from "../../services/kioskServiceSession";
import { useKioskToast, KioskToast } from "../../components/useKioskToast";
import { limitMessage } from "../../utils/apparatPolicy";

// Заказ чистых листов A4 — отдельная услуга без печати документа.
// Изображение бумаги отделено от выбора количества.
//
// Документ — системная строка на бэкенде (GET /api/v1/documents/blank,
// kind="blank"), создаётся один раз лениво и переиспользуется всеми заказами.
// Цена — priceApparat.price_for_blank_sheet (см. /api/v1/payments/price).
const MAX_SHEETS = 15;
const FALLBACK_PRICE = 30; // пока priceApparat ещё не загрузился

const blockVariants = {
    hidden: { opacity: 0, y: 24 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.45, delay: i * 0.1 },
    }),
};

function BlankSheet() {
    const freePrint = isFreePrintEnabled();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [sheets, setSheets] = useState(1);
    // null = ответ ещё не пришёл (или запрос упал) — состояние "неизвестно",
    // отдельное от "бумаги 0". Раньше оба случая жили в одном числе, из-за
    // чего упавший запрос молча открывал лимит в MAX_SHEETS при пустом лотке.
    const [availablePapers, setAvailablePapers] = useState(null);
    // Подтверждение перед оплатой — люди путают чистые листы со своим
    // документом: лишний шаг с явным
    // "это пустые листы, без файла" перед списанием денег отсекает случайный тап.
    const [showConfirm, setShowConfirm] = useState(false);
    const { toastMsg, showToast } = useKioskToast();

    const { blankDocument } = useSelector((state) => state.documentsList);
    const { priceApparat, loadingPayment } = useSelector((state) => state.payments);

    useEffect(() => {
        dispatch(fetchBlankDocument());
        dispatch(fetchPriceApparat());
    }, [dispatch]);

    // Тот же источник остатка бумаги, что и на экране параметров печати.
    useEffect(() => {
        import("axios").then(({ default: axios }) => {
            const deviceToken = localStorage.getItem("deviceToken");
            axios
                .get(
                    `${process.env.REACT_APP_BASEURL}/api/v1/admin/apparats/pages`,
                    {
                        headers: { "Device-Token": `${deviceToken}` },
                    },
                )
                .then((response) =>
                    // max_tray_pages — максимум среди лотков, см. комментарий
                    // в EditDoc.jsx. Чистый лист — всегда один документ, поэтому
                    // сравнение с одним лотком здесь корректно без оговорок.
                    setAvailablePapers(
                        response.data.max_tray_pages ??
                            response.data.count_pages ??
                            0,
                    ),
                )
                .catch((error) => {
                    console.error("Error fetching available papers:", error);
                    // availablePapers остаётся null ("неизвестно") — не 0.
                    // Иначе упавший запрос выглядел бы как пустой лоток.
                });
        });
    }, []);

    const papersKnown = availablePapers !== null;
    // Пока не знаем остаток — не блокируем оплату лимитом (последнее слово
    // всё равно за сервером в create_payment), но и не открываем счётчик
    // выше делового потолка MAX_SHEETS.
    const paperLimit = papersKnown ? Math.max(1, availablePapers) : MAX_SHEETS;
    const limit = Math.min(MAX_SHEETS, paperLimit);

    // Если данные о бумаге пришли уже после того, как пользователь поднял
    // счётчик (пока лимит ещё был "неизвестно" = MAX_SHEETS) — подрезаем до
    // реального лимита, а не оставляем цифру, которую нельзя оплатить.
    useEffect(() => {
        if (papersKnown) {
            setSheets((prev) => Math.min(prev, limit));
        }
    }, [papersKnown, limit]);

    const changeSheets = (delta) =>
        setSheets((prev) => Math.min(limit, Math.max(1, prev + delta)));

    const handleIncrement = () => {
        if (sheets < limit) {
            changeSheets(1);
            return;
        }
        // Упёрлись в потолок — молчать нельзя, иначе кнопка выглядит сломанной.
        // Различаем причину: реальная нехватка бумаги vs деловой лимит на заказ.
        if (papersKnown && sheets >= availablePapers) {
            showToast(
                t("editDoc.blank.toast_paper_limit", { count: availablePapers }),
            );
        } else {
            showToast(t("editDoc.blank.toast_max_sheets", { count: MAX_SHEETS }));
        }
    };

    // "Нет бумаги вовсе" и "бумаги меньше, чем выбрано" — оба варианта
    // недостачи, оба должны блокировать оплату и показывать плашку.
    const showWarning =
        papersKnown && (availablePapers <= 0 || availablePapers < sheets);
    // accept_orders — единственный из режимов удалённого управления, который
    // применяется к чистым листам: is_color/allow_duplex/allow_photo_print
    // сервер для тарифа blank не проверяет (см. _reject_if_mode_disabled).
    const ordersPaused = priceApparat?.accept_orders === false || (!freePrint && priceApparat?.paid_orders_available === false);
    const pricePerSheet = priceApparat?.price_for_blank_sheet ?? FALLBACK_PRICE;
    const total = sheets * pricePerSheet;
    const policyMessage = freePrint
        ? null
        : limitMessage(priceApparat, { sheets, copies: 1, documents: 1 }, t);
    const canPay =
        Boolean(blankDocument?.id) && !showWarning && !loadingPayment && !ordersPaused && !policyMessage;

    const handlePayment = async () => {
        if (!blankDocument?.id) return;
        try {
            const resultAction = await dispatch(
                createPaymentAction([
                    {
                        document_id: blankDocument.id,
                        amount: total,
                        custom_pages: "",
                        // count_pages — списание бумаги и цена (сервер считает
                        // сумму сам, см. price_for_blank_sheet × count_pages).
                        count_pages: sheets,
                        // copy_count — сколько раз киоск напечатает один и тот
                        // же одностраничный PDF; сервер НЕ домножает на него
                        // сумму для тарифа blank (см. payments_crud.py).
                        copy_count: sheets,
                        is_all_pages: true,
                        is_album_orientation: false,
                        is_one_side: true,
                        // Прямой смысл: false = без цвета. Сервер для тарифа
                        // blank всё равно форсит False (payments_crud.py),
                        // но шлём честно.
                        is_color: false,
                        status: "pending",
                    },
                ]),
            );
            if (createPaymentAction.fulfilled.match(resultAction)) {
                if (resultAction.payload?.provider === "admin_free") {
                    navigate("/service/print-sent", { replace: true });
                } else {
                    navigate("/payments", {
                        state: { from: "/printing/blank_sheet" },
                    });
                }
            } else {
                console.error(
                    "Ошибка создания платежа:",
                    resultAction.payload || t("editDoc.payment_error"),
                );
            }
        } catch (error) {
            console.error(
                "Ошибка при отправке запроса на сервер:",
                error.message,
            );
        }
    };

    return (
        <div className="h-[88vh] px-[2.4vw] py-[2vh] flex flex-col gap-[1.2vh] bg-[#EDEFF4]">
            <header className="flex-none flex items-center gap-[2vw]">
                <Link data-analytics="blanksheet.editDoc.blank.back_to_files.1" to="/printing/show_list/user" className="rounded-full bg-white px-[2vw] py-[1.3vh] text-[1.5vh] font-bold text-[#246CE2]">
                    ← {t("editDoc.blank.back_to_files")}
                </Link>
                <span className="ml-auto rounded-full bg-[#E3ECFB] px-[1.4vw] py-[0.6vh] text-[1.2vh] font-bold text-[#246CE2]">{t("listDoc.blank_chip")}</span>
            </header>
            <motion.section variants={blockVariants} initial="hidden" animate="visible" custom={0}
                className="flex-1 min-h-0 overflow-y-auto bg-white rounded-[3vh] px-[3vw] py-[2.5vh] flex flex-col gap-[2vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)]">
                <div className="text-center">
                    <h1 className="text-[2.8vh] leading-tight tracking-tight font-extrabold text-[#12161F]">{t("editDoc.blank.heading")}</h1>
                    <p className="text-[1.8vh] font-semibold text-[#7B8290] mt-[0.8vh]">{t("editDoc.blank.subtitle")}</p>
                </div>
                <div className="flex-1 min-h-[24vh] flex items-center justify-center gap-[5vw] rounded-[2vh] bg-[#F1F3F7] px-[3vw] py-[2vh]">
                    <figure className="flex flex-col items-center gap-[1vh]">
                        <BlankPaperPreview className="h-[20vh] w-[16vh] max-w-[25vw]" />
                        <figcaption className="text-[1.4vh] font-bold text-[#7B8290]">A4 · 210 × 297 {t("editDoc.blank.mm")}</figcaption>
                    </figure>
                    <div className="max-w-[42vw]">
                        <h2 className="text-[2.5vh] leading-tight font-extrabold text-[#12161F]">{t("editDoc.blank.paper_only")}</h2>
                        <p className="text-[1.7vh] font-semibold leading-snug mt-[1vh] text-[#7B8290]">{t("editDoc.blank.no_file_note")}</p>
                        <Link data-analytics="blanksheet.editDoc.blank.print_document.2" to="/printing/show_list/user" className="inline-block text-[1.5vh] font-bold text-[#246CE2] underline underline-offset-4 mt-[2vh]">
                            {t("editDoc.blank.print_document")}
                        </Link>
                    </div>
                </div>
                <div className="flex-none text-center">
                    <h2 className="text-[2vh] font-bold">{t("editDoc.blank.title")}</h2>
                    <div className="relative flex items-center justify-center gap-[3vw] mt-[1.2vh]">
                        <KioskToast message={toastMsg} position="bottom" />
                        <button data-analytics="blanksheet.changeSheets.3" type="button" onClick={() => changeSheets(-1)} disabled={sheets <= 1} aria-label={t("editDoc.blank.decrease")}
                            className="w-[7vh] h-[7vh] rounded-[2vh] bg-[#F1F3F7] text-[4vh] font-bold disabled:opacity-30 active:bg-[#E3ECFB]">−</button>
                        <div className="min-w-[14vh]" aria-live="polite" aria-atomic="true">
                            <span className="block text-[5vh] font-extrabold leading-none text-[#246CE2]">{sheets}</span>
                            <span className="text-[1.5vh] font-semibold text-[#7B8290]">{t("editDoc.blank.sheets", { count: sheets })}</span>
                        </div>
                        <button data-analytics="blanksheet.handleIncrement.4" type="button" onClick={handleIncrement} aria-disabled={sheets >= limit} aria-label={t("editDoc.blank.increase")}
                            className={`w-[7vh] h-[7vh] rounded-[2vh] bg-[#F1F3F7] text-[4vh] font-bold active:bg-[#E3ECFB] ${sheets >= limit ? "opacity-30" : ""}`}>+</button>
                    </div>
                    <p className="text-[1.4vh] text-[#7B8290] font-semibold mt-[1vh]">{t("editDoc.blank.unit_price", { price: freePrint ? 0 : pricePerSheet })}</p>
                </div>
            </motion.section>
            {showWarning && <p role="alert" className="flex-none rounded-[1.6vh] bg-[#F7E1DC] text-[#963E2D] px-[2vw] py-[1vh] text-[1.5vh] font-bold text-center">
                {t("editDoc.blank.paper_warning", { count: availablePapers })}
            </p>}
            <ApparatModeWarning ordersPaused={ordersPaused} photoBlocked={false} message={policyMessage} t={t} />

            {/* Итог и оплата */}
            <motion.div
                variants={blockVariants}
                initial="hidden"
                animate="visible"
                custom={2}
                className="flex-none bg-white rounded-[3vh] shadow-[0_0.5vh_2vh_rgba(18,22,31,0.08)] py-[1.4vh] px-[2.4vw] flex items-center gap-[2vw]"
            >
                <div className="min-w-0">
                    <span className="text-[1.1vh] font-bold uppercase tracking-[0.15em] text-[#7B8290]">
                        {t("editDoc.blank.summary", { count: sheets })}
                    </span>
                    <div className="text-[4.2vh] font-extrabold tracking-tight text-[#246CE2] leading-none mt-[0.4vh]">
                        {freePrint ? 0 : total} ₸
                    </div>
                </div>
                <button data-analytics="blanksheet.editDoc.blank.free_action.5"
                    type="button"
                    onClick={() => setShowConfirm(true)}
                    disabled={!canPay}
                    className="ml-auto max-w-[48vw] flex items-center gap-[0.8vw] bg-[#246CE2] text-white rounded-full py-[1.4vh] px-[2.4vw] text-[1.8vh] font-bold disabled:opacity-40"
                >
                    {freePrint ? t("editDoc.blank.free_action") : t("editDoc.blank.buy_action")}
                    <svg
                        className="w-[2.1vh] h-[2.1vh]"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        strokeWidth="2.4"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <path d="M5 12h14M13 6l6 6-6 6" />
                    </svg>
                </button>
            </motion.div>

            <AnimatePresence>
                {showConfirm && (
                    <KioskModal
                        tone="accent"
                        title={t("editDoc.blank.confirm_title")}
                        titleKey={sheets}
                        description={t("editDoc.blank.confirm_desc", {
                            count: sheets,
                            total: freePrint ? 0 : total,
                        })}
                        icon={<BlankPaperPreview className="h-[7vh] w-[6vh]" />}
                        footer={
                            <div className="flex flex-wrap gap-[1vw]">
                                <button data-analytics="blanksheet.editDoc.blank.confirm_cancel.6"
                                    type="button"
                                    onClick={() => setShowConfirm(false)}
                                    disabled={loadingPayment}
                                    className="flex-1 py-[1.8vh] rounded-full bg-[#F1F3F7] text-[1.8vh] font-bold text-[#12161F] active:bg-[#E1E4EB] transition-colors"
                                >
                                    {t("editDoc.blank.confirm_cancel")}
                                </button>
                                <button data-analytics="blanksheet.editDoc.blank.free_action.7"
                                    type="button"
                                    onClick={handlePayment}
                                    disabled={!canPay}
                                    className="flex-1 py-[1.8vh] rounded-full bg-[#246CE2] text-[1.8vh] font-bold text-white disabled:opacity-50 active:bg-[#1B55B4] transition-colors"
                                >
                                    {freePrint
                                        ? t("editDoc.blank.free_action")
                                        : t("editDoc.blank.confirm_pay", { total })}
                                </button>
                                <Link data-analytics="blanksheet.editDoc.blank.print_document.8" to="/printing/show_list/user"
                                    onClick={(event) => { if (loadingPayment) event.preventDefault(); }}
                                    aria-disabled={loadingPayment}
                                    className="w-full py-[1vh] text-[1.5vh] font-bold text-[#246CE2] underline">
                                    {t("editDoc.blank.print_document")}
                                </Link>
                            </div>
                        }
                    >
                        <p className="w-full mt-[2vh] rounded-[1.6vh] bg-[#E3ECFB] text-[#246CE2] px-[2vw] py-[1.5vh] text-[2vh] font-bold">
                            {t("editDoc.blank.no_file_note")}
                        </p>
                    </KioskModal>
                )}
            </AnimatePresence>
        </div>
    );
}

export default BlankSheet;
